// Plan Library two-person review (DEV-388, migration 0147).
// See shared/utils/planReview.ts for the status machine and readiness rules.
import {
  pgTable,
  serial,
  bigserial,
  integer,
  text,
  boolean,
  varchar,
  jsonb,
  timestamp,
  index,
} from "drizzle-orm/pg-core";
import { plans } from "./plans";

/** Rate rows a pending version proposes (same shape as the library PATCH body). */
export interface PendingRateRow {
  rateStructureType: string;
  coverageTier?: string | null;
  ageBand?: string | null;
  ageBandType?: string | null;
  ratingAreaId?: number | null;
  ratingAreaLabel?: string | null;
  coverageTable?: string | null;
  monthlyRate: string;
}

/**
 * What a pending version would change on the live plan. Only keys present are
 * applied on approval: `fields` → plans columns, `rawBenefits` →
 * plans.raw_benefits, `benefits` → plan_benefits rows (replaced), `rates` →
 * a published rate version at `effectiveStart`.
 */
export interface PendingVersionProposal {
  fields?: Record<string, unknown>;
  rawBenefits?: Array<{ name: string; value: string; network?: "in" | "out"; deductibleApply?: boolean }> | null;
  benefits?: Array<Record<string, unknown>>;
  rates?: { effectiveStart: string; rows: PendingRateRow[] };
}

// R9: one open pending version per plan (partial unique index in 0147).
// status: draft | needs_review | changes_requested (open) | applied | discarded.
export const planPendingVersions = pgTable(
  "plan_pending_versions",
  {
    id: serial("id").primaryKey(),
    planId: integer("plan_id")
      .notNull()
      .references(() => plans.id, { onDelete: "cascade" }),
    status: text("status").notNull().default("needs_review"),
    urgent: boolean("urgent").notNull().default(false),
    proposed: jsonb("proposed").$type<PendingVersionProposal>().notNull().default({}),
    changedSections: text("changed_sections").array().notNull().default([]),
    createdById: varchar("created_by_id"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    resolvedAt: timestamp("resolved_at"),
    resolvedById: varchar("resolved_by_id"),
  },
  (t) => ({
    planIdx: index("plan_pending_versions_plan_idx").on(t.planId),
  }),
);

export type PlanPendingVersion = typeof planPendingVersions.$inferSelect;
export type InsertPlanPendingVersion = typeof planPendingVersions.$inferInsert;

// R10: append-only review audit log. UPDATE/DELETE/TRUNCATE are rejected by a
// trigger (0147), and there is no app API to change rows. No FK to plans: the
// history outlives the plan row.
export const planReviewEvents = pgTable(
  "plan_review_events",
  {
    id: bigserial("id", { mode: "number" }).primaryKey(),
    planId: integer("plan_id").notNull(),
    pendingVersionId: integer("pending_version_id"),
    // created | edited | submitted | withdrawn | approved | rejected |
    // pending_created | pending_updated | pending_discarded | live_edited |
    // form_attached | form_detached | grandfathered
    eventType: text("event_type").notNull(),
    fromStatus: text("from_status"),
    toStatus: text("to_status"),
    note: text("note"),
    urgent: boolean("urgent").notNull().default(false),
    actorUserId: varchar("actor_user_id"),
    // Snapshot of the actor's display name at the time (users can be renamed).
    actorName: text("actor_name"),
    metadata: jsonb("metadata"),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (t) => ({
    planIdx: index("plan_review_events_plan_idx").on(t.planId, t.createdAt),
  }),
);

export type PlanReviewEvent = typeof planReviewEvents.$inferSelect;
export type InsertPlanReviewEvent = typeof planReviewEvents.$inferInsert;
